import {
  AutoChargeDetails,
  SUBSCRIPTION_TYPE,
  TCard,
  TDefaultPaymentMethod,
  TPaymentMethodDetails,
  TPlanList,
  TProductType,
  TSupportedFeature
} from 'typings';

export type TFreeTrialState = {
  isEligibleForFreeTrialData: boolean;
  isEligibleForFreeTrialHomeSecurity: boolean;
  isEligibleForFreeTrialBird: boolean;
  isEligibleForFreeTrialPet: boolean;
  isEligibleForFreeTrialTrail: boolean;
  isEligibleForFreeTrialBaby: boolean;
  isLoading: boolean;
};

export type TSubScriptionState = {
  subscriptionData: TSubscriptionType[];
  homeSecuritySubscriptionList: TSubscriptionType[];
  birdSubscriptionList: TSubscriptionType[];
  petSubscriptionList: TSubscriptionType[];
  trailSubscriptionList: TSubscriptionType[];
  babySubscriptionList: TSubscriptionType[];
  // Space-scoped Data subscriptions (independent of product_id).
  dataSubscriptionList: TSubscriptionType[];
  isLoading: boolean;
  selectedSubscriptionInfo: TSelectedSubscriptionInfo;
  defaultPaymentMethod: TDefaultPaymentMethod | null;
  birdDefaultPaymentMethod: TDefaultPaymentMethod | null;
  petDefaultPaymentMethod: TDefaultPaymentMethod | null;
  trailDefaultPaymentMethod: TDefaultPaymentMethod | null;
  babyDefaultPaymentMethod: TDefaultPaymentMethod | null;
  selectedSKUInfo?: TSelectedSKUInfo | null;
};

export type TModifyAction = 'upgrade' | 'downgrade';

export type TSelectedSKUInfo = {
  selectedSKU: TPlanList;
  previousSubscription: TSubscriptionType;
  trial: boolean;
  action: TModifyAction;
};

export enum ModifyAction {
  UPGRADE = 'upgrade',
  DOWNGRADE = 'downgrade'
}

export type TSelectedSubscriptionInfo = {
  isUpgrade: boolean;
  isFreeTrial: boolean;
  planSelected: TPlanList;
  purchaseId: string;
  deviceId: string;
  previousSubscriptionSKU?: string;
  autoChargeDataDetails?: AutoChargeDetails;
} | null;

export type TSubscriptionType = {
  id: string;
  user_id: string;
  product_id: TProductType;
  category: string;
  sku_id: string;
  space_id: string;
  space_name: string;
  sku_level: number;
  sku_category: string;
  // Data SKUs are space-scoped and independent of product_id (scope = 'Space').
  scope?: TSubscriptionScope;
  devices: TDevice[];
  sku_name: string;
  subscription_cycle: number;
  subscribe_status: string;
  pay_status?: string;
  // Spec "List Purchases": the latest purchase for a product. Retry / re-subscribe
  // only apply when this is the latest purchase (see "Retry vs Re-subscribe logic").
  is_latest_purchase?: boolean;
  currency: string;
  paid_amount: number;
  next_bill_amount?: number;
  pay_type: string;
  trial: boolean;
  current_sku_amount: number;
  subscription_type: SUBSCRIPTION_TYPE;
  pay_date?: string;
  start_date?: number;
  end_date?: number;
  next_billing_date?: number;
  supported_features: TSupportedFeature[];
  card_details?: TCard;
  /** Spec shape — wraps card/cashapp; prefer this `.id` for hydration keys. */
  payment_method_details: TPaymentMethodDetails;
  trial_start_date?: string;
  last_payment_date?: number;
  cancel_date?: number;
  purchase_id?: string;
  next_billing_cycle_sku?: {
    id: string;
    name: string;
  };
  // Multiple Plan Subscriptions (FCM-2916). Read these only through
  // utils/multiPlan.
  /** Active number of plans (1–5). Old subscriptions return 1. */
  quantity?: number;
  /**
   * Plans from the next billing date. Lower than `quantity` = a decrease is
   * scheduled for `next_billing_date`.
   */
  next_cycle_quantity?: number;
};

export type TPurchasedProductType = {
  id: string;
  product_id: TProductType;
  sku_id: string;
  sku_level: number;
  sku_category: string;
  sku_name: string;
  subscription_cycle: number;

  // optional fields in case of subscriptions
  subscription_type?: SUBSCRIPTION_TYPE;
  supported_features?: TSupportedFeature[];
  trial?: boolean;
  next_billing_date?: number;
  start_date?: number;
  payment_method_details?: TPaymentMethodDetails;
};

export type TDevice = {
  id: string;
  name: string;
};

export type TSubscriptionBuyType = {
  id: string;
  status: string;
  client_secret: string;
  sku_id: string;
};

export type TPrimeSubscriptionBuyResponse = {
  subscription_id: string;
};

export type TProps_BuySubscription = {
  selectedSubscription?: TSubscriptionType | null;
  togglePrimaryModal?: () => void;
  isUpgrade?: boolean;
  isFreeTrial?: boolean;
  deviceId?: string;
};

export type TTrialResponse = {
  trial: boolean;
};

export type TCreateCheckoutSessionPayload = {
  purchase_id: string;
  locale: string;
  branding_settings?: {
    background_color?: string;
    button_color?: string;
  };
  data_pass_discount_enabled?: boolean;
  coupan?: string;
};

export type TCheckoutSessionResponse = {
  client_secret: string;
};

export type TUpgradeSubscriptionPayload = {
  // action: string;
  // end_date?: string;
  // next_billing_date?: string;
  payment_method_id: string;
  sku_id: string;
  // status: string;
};

export const DATA_PASS_STATUS = {
  INIT: 'Init',
  ACTIVE: 'Active'
} as const;

export type TDataPassStatus =
  (typeof DATA_PASS_STATUS)[keyof typeof DATA_PASS_STATUS];

export type TCurrentDataPass = {
  id: string;
  sku_name: string;
  sku_level: number;
  sku_category: TSubscriptionCategories;
  subscription_cycle: number;
  total_data: number;
  status: TDataPassStatus;
  expiry_time?: number;
};

// Scope of a subscription. Data subscriptions are scoped to the space rather
// than to a product/device.
export const SUBSCRIPTION_SCOPE = {
  SPACE: 'Space',
  PRODUCT: 'Product'
} as const;

export type TSubscriptionScope =
  (typeof SUBSCRIPTION_SCOPE)[keyof typeof SUBSCRIPTION_SCOPE];

export const SUBSCRIPTION_CATEGORY = {
  DATA: 'Data',
  AI: 'AI',
  COMBO: 'Combo'
} as const;

export type TSubscriptionCategories =
  (typeof SUBSCRIPTION_CATEGORY)[keyof typeof SUBSCRIPTION_CATEGORY];

// Response shape (Patch + Get). The auto top-up contract references the card by
// id only (`payment_method_id`), not an embedded payment-method object.
export type TAutoTopUpDataDetails = {
  id: string;
  space_id: string;
  enable: boolean;
  sku_id: string;
  sku_name?: string;
  discount_id?: string;
  recharge_cycle?: number;
  limit?: number;
  // RFC3339-described in the API doc but delivered as epoch milliseconds.
  next_reset_at?: number;
  payment_method_id?: string;
};

// Patch (Update) payload — all fields optional; only sent fields are updated.
export type TAutoTopUpDataPayload = {
  enable?: boolean;
  sku_id?: string;
  discount_id?: string;
  recharge_cycle?: number;
  limit?: number;
  payment_method_id?: string;
};

// Create payload — payment_method_id is required (even when disabling).
export type TCreateAutoTopUpDataPayload = {
  enable: boolean;
  sku_id?: string;
  discount_id?: string;
  recharge_cycle?: number;
  limit?: number;
  payment_method_id: string;
};

// Remaining data in the space's pool.
export type TGetPoolDataRes = {
  total_data_mb: number;
  remaining_data_mb: number;
};

// Standard Atlas error body for these endpoints.
export type TAutoTopUpErrorResponse = {
  code: string;
  message: string;
};
